import { getPassColor } from "@/pages/catalogs/pages/edger-rolls/components/passes/PassesGrid";
import { routePartsManager } from "@/setup/routes/report.routes";
import {
	Box,
	Card,
	CardContent,
	Chip,
	Divider,
	Grid2,
	Tooltip,
	Typography,
} from "@mui/material";
import { ReactNode } from "react";
import { Carrier } from "../models/Carrier";
import { assemblyStatusCodes } from "./CarrierAssemblyStatusTag";

const getTooltipText = (carrier: Carrier, position: "top" | "bottom") => {
	if (position == "bottom") {
		return carrier?.BottomRollSelectedPassNumber
			? `Pass ${carrier.BottomRollSelectedPassNumber} with status ${carrier.BottomRollSelectedPassStatus == "R" ? "ready" : "damaged"}`
			: "";
	}

	return carrier?.TopRollSelectedPassNumber
		? `Pass ${carrier.TopRollSelectedPassNumber} with status ${carrier.TopRollSelectedPassStatus == "R" ? "ready" : "damaged"}`
		: "";
};

const Field = ({ label, children }: { label: string; children: ReactNode }) => (
	<Grid2 size={{ xs: 6, sm: 4, md: "grow" }}>
		<Typography variant="body2" color="textSecondary">
			{label}
		</Typography>
		{children}
	</Grid2>
);

export const CarrierCard = ({
	carrier,
	footer,
}: {
	carrier: Carrier | null;
	footer?: ReactNode;
}) => {
	const status = assemblyStatusCodes.find(
		(s) => s.Code === carrier?.CarrierStatusCode,
	);

	return (
		<Card
			style={{
				border: "2px solid #FAFAFA",
				borderRadius: 10,
				width: "100%",
				marginBottom: 5,
			}}
			elevation={2}
		>
			<CardContent>
				<Box display="flex" alignItems="flex-end" gap={1.5} mb={2}>
					<Box display="flex" flexDirection="column">
						<Typography variant="caption" color="textSecondary" mb={-0.6}>
							Carrier
						</Typography>
						<Typography variant="h5" fontWeight="bold">
							{carrier?.PartName}
						</Typography>
					</Box>
					<Chip
						label={status?.Name}
						size="small"
						sx={{
							backgroundColor: status?.Color,
							color: status?.TextColor,
							fontWeight: "bold",
							mb: 0.5,
						}}
					/>
				</Box>
				<Grid2 container spacing={2}>
					<Field label="Section">
						<Typography variant="body1">
							{carrier?.SectionName ?? "-"}
						</Typography>
					</Field>
					<Field label="Stand">
						<Typography variant="body1">{carrier?.StandName ?? "-"}</Typography>
					</Field>
					<Field label="Rollset letter">
						<Typography variant="body1">
							{carrier?.RollsetLetter ?? "-"}
						</Typography>
					</Field>
					<Field label="Top roll">
						<Typography
							variant="body1"
							sx={{
								color: "#1976d2",
								cursor: "pointer",
								lineHeight: 1.2,
							}}
							onClick={() => {
								window.open(
									`${routePartsManager}/${carrier?.TopRollId}`,
									"_blank",
								);
							}}
						>
							{carrier?.TopRollName ?? "-"}
						</Typography>
					</Field>
					<Field label="Bottom roll">
						<Typography
							variant="body1"
							sx={{
								color: "#1976d2",
								cursor: "pointer",
								lineHeight: 1.2,
							}}
							onClick={() => {
								window.open(
									`${routePartsManager}/${carrier?.BottomRollId}`,
									"_blank",
								);
							}}
						>
							{carrier?.BottomRollName ?? "-"}
						</Typography>
					</Field>
					{carrier?.TopRollSelectedPassNumber ? (
						<Field label="Selected pass">
							<Tooltip title={getTooltipText(carrier!, "bottom")}>
								<Typography
									variant="body1"
									sx={{
										color: (theme) =>
											getPassColor(carrier!.TopRollSelectedPassStatus!, theme)
												.main,
										lineHeight: 1.2,
									}}
									data-test-id={`roll-pass-${carrier?.TopRollSelectedPassNumber}`}
								>
									{`Pass ${carrier.TopRollSelectedPassNumber}`}
								</Typography>
							</Tooltip>
						</Field>
					) : null}
				</Grid2>
			</CardContent>
			{footer && (
				<>
					<Divider />
					<Box sx={{ px: 2, py: 1.5 }}>{footer}</Box>
				</>
			)}
		</Card>
	);
};
